import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ApiService } from '../../services/api.service';
import { ProfessionalStateService } from '../../services/professional-state.service';
import { NotificationService } from '../../services/notification.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-accounts-receivable',
  templateUrl: './accounts-receivable.component.html',
  styleUrls: ['./accounts-receivable.component.scss']
})
export class AccountsReceivableComponent implements OnInit, OnDestroy {
  result: any = { items: [], totalCount: 0, page: 1, pageSize: 20 };
  loading = false;
  professionalId = 0;
  filter: any = { page: 1, pageSize: 20, search: '', isActive: true };

  showModal = false;
  saving = false;
  editingId: number | null = null;
  form: any = {
    patientId: null, healthInsuranceId: null, categoryId: '', costCenterId: '',
    description: '', amount: '', competenceDate: '', dueDate: '',
    paymentMethod: '', status: 'Pendente'
  };

  // Lists for dropdowns
  patients: any[] = [];
  healthInsurances: any[] = [];

  // Datepicker - Competence Date
  showCompetenceDatePicker = false;
  competenceDatePickerCurrentDate = new Date();
  competenceDatePickerDays: Date[] = [];
  competenceDatePickerTop = '0px';
  competenceDatePickerLeft = '0px';
  competenceDateDisplay = '';

  // Datepicker - Due Date
  showDueDatePicker = false;
  dueDatePickerCurrentDate = new Date();
  dueDatePickerDays: Date[] = [];
  dueDatePickerTop = '0px';
  dueDatePickerLeft = '0px';
  dueDateDisplay = '';

  weekDays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

  @ViewChild('competenceDateInput', { read: ElementRef }) competenceDateInput?: ElementRef;
  @ViewChild('dueDateInput', { read: ElementRef }) dueDateInput?: ElementRef;

  private searchInput$ = new Subject<string>();
  private subs = new Subscription();

  constructor(
    private api: ApiService,
    private profState: ProfessionalStateService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.searchInput$.pipe(debounceTime(400), distinctUntilChanged())
        .subscribe(term => { this.filter.search = term; this.filter.page = 1; this.load(); })
    );

    this.subs.add(
      this.profState.selected$.subscribe(p => {
        if (p) {
          this.professionalId = p.id;
          this.load();
          this.loadPatients();
          this.loadHealthInsurances();
        }
      })
    );
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  load(): void {
    this.loading = true;
    this.api.getAccountsReceivable(this.professionalId, this.filter).subscribe({
      next: r => { this.result = r; this.loading = false; },
      error: () => this.loading = false
    });
  }

  loadPatients(): void {
    this.api.getPatients(this.professionalId, { page: 1, pageSize: 500, sortBy: 'name' }).subscribe({
      next: r => this.patients = r.items,
      error: () => {}
    });
  }

  loadHealthInsurances(): void {
    this.api.getHealthInsurances(this.professionalId, { page: 1, pageSize: 200 }).subscribe({
      next: r => this.healthInsurances = r.items,
      error: () => {}
    });
  }

  onSearch(term: string): void { this.searchInput$.next(term); }
  onPageChange(page: number): void { this.filter.page = page; this.load(); }
  onSizeChange(size: number): void { this.filter.pageSize = size; this.filter.page = 1; this.load(); }

  openModal(accountReceivable?: any): void {
    this.editingId = accountReceivable?.id ?? null;
    this.form = accountReceivable ? { ...accountReceivable } : {
      patientId: null, healthInsuranceId: null, categoryId: '', costCenterId: '',
      description: '', amount: '', competenceDate: '', dueDate: '',
      paymentMethod: '', status: 'Pendente'
    };

    // Initialize competenceDate display
    if (this.form.competenceDate) {
      const d = this.form.competenceDate.substring(0, 10);
      const parts = d.split('-');
      if (parts.length === 3) this.competenceDateDisplay = `${parts[2]}/${parts[1]}/${parts[0]}`;
    } else {
      this.competenceDateDisplay = '';
    }

    // Initialize dueDate display
    if (this.form.dueDate) {
      const d = this.form.dueDate.substring(0, 10);
      const parts = d.split('-');
      if (parts.length === 3) this.dueDateDisplay = `${parts[2]}/${parts[1]}/${parts[0]}`;
    } else {
      this.dueDateDisplay = '';
    }

    this.showCompetenceDatePicker = false;
    this.showDueDatePicker = false;
    this.showModal = true;
  }

  save(): void {
    if (!this.form.description?.trim()) {
      this.notification.warning('Atenção', 'Descrição é obrigatória');
      return;
    }

    this.saving = true;
    const op = this.editingId
      ? this.api.updateAccountReceivable(this.professionalId, this.editingId, this.form)
      : this.api.createAccountReceivable(this.professionalId, this.form);

    op.subscribe({
      next: () => {
        this.saving = false;
        this.showModal = false;
        this.notification.success('Sucesso!', `Conta a receber ${this.editingId ? 'atualizada' : 'cadastrada'} com sucesso`);
        this.load();
      },
      error: () => {
        this.saving = false;
        this.notification.error('Erro', 'Erro ao salvar conta a receber');
      }
    });
  }

  delete(accountReceivable: any): void {
    this.notification.confirm(
      'Confirmar Exclusão',
      `Tem certeza que deseja excluir a conta a receber ${accountReceivable.description}?`,
      () => {
        this.api.deleteAccountReceivable(this.professionalId, accountReceivable.id).subscribe({
          next: () => {
            this.notification.success('Sucesso!', 'Conta a receber excluída com sucesso');
            this.load();
          },
          error: () => this.notification.error('Erro', 'Erro ao excluir conta a receber')
        });
      }
    );
  }

  fmtDate(d?: string): string {
    if (!d) return '-';
    const date = new Date(d);
    return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  }

  // ═══════════════════════════════════════════════════════════════
  // DATEPICKER - COMPETENCE DATE
  // ═══════════════════════════════════════════════════════════════

  toggleCompetenceDatePicker(): void {
    this.showCompetenceDatePicker = !this.showCompetenceDatePicker;
    this.showDueDatePicker = false;
    if (this.showCompetenceDatePicker) {
      if (this.form.competenceDate) {
        const parts = this.competenceDateDisplay.split('/');
        if (parts.length === 3) {
          this.competenceDatePickerCurrentDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        }
      } else {
        this.competenceDatePickerCurrentDate = new Date();
      }
      this.generateCompetenceDateCalendar();
      setTimeout(() => {
        if (this.competenceDateInput) {
          const rect = this.competenceDateInput.nativeElement.getBoundingClientRect();
          this.competenceDatePickerTop = `${rect.bottom + 8}px`;
          this.competenceDatePickerLeft = `${rect.left}px`;
        }
      }, 0);
    }
  }

  generateCompetenceDateCalendar(): void {
    const year = this.competenceDatePickerCurrentDate.getFullYear();
    const month = this.competenceDatePickerCurrentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();
    const days: Date[] = [];
    for (let i = startDay - 1; i >= 0; i--) days.push(new Date(year, month, -i));
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) days.push(new Date(year, month + 1, d));
    this.competenceDatePickerDays = days;
  }

  competenceDatePickerPreviousMonth(): void {
    this.competenceDatePickerCurrentDate = new Date(
      this.competenceDatePickerCurrentDate.getFullYear(),
      this.competenceDatePickerCurrentDate.getMonth() - 1, 1);
    this.generateCompetenceDateCalendar();
  }

  competenceDatePickerNextMonth(): void {
    this.competenceDatePickerCurrentDate = new Date(
      this.competenceDatePickerCurrentDate.getFullYear(),
      this.competenceDatePickerCurrentDate.getMonth() + 1, 1);
    this.generateCompetenceDateCalendar();
  }

  getCompetenceDatePickerMonthYear(): string {
    return this.competenceDatePickerCurrentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }

  competenceDatePickerIsCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.competenceDatePickerCurrentDate.getMonth();
  }

  competenceDatePickerIsToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  competenceDatePickerIsSelected(date: Date): boolean {
    if (!this.form.competenceDate) return false;
    const parts = this.competenceDateDisplay.split('/');
    if (parts.length !== 3) return false;
    return date.getDate() === parseInt(parts[0]) &&
           date.getMonth() === parseInt(parts[1]) - 1 &&
           date.getFullYear() === parseInt(parts[2]);
  }

  competenceDatePickerSelectDate(date: Date): void {
    const pad = (n: number) => String(n).padStart(2, '0');
    this.form.competenceDate = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    this.competenceDateDisplay = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
    this.showCompetenceDatePicker = false;
  }

  competenceDatePickerSelectToday(): void {
    this.competenceDatePickerSelectDate(new Date());
  }

  onCompetenceDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    if (value.length >= 2) value = value.substring(0, 2) + '/' + value.substring(2);
    if (value.length >= 5) value = value.substring(0, 5) + '/' + value.substring(5, 9);
    this.competenceDateDisplay = value;
    if (value.length === 10) {
      const parts = value.split('/');
      const day = parseInt(parts[0]), month = parseInt(parts[1]), year = parseInt(parts[2]);
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const pad = (n: number) => String(n).padStart(2, '0');
        this.form.competenceDate = `${year}-${pad(month)}-${pad(day)}`;
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // DATEPICKER - DUE DATE
  // ═══════════════════════════════════════════════════════════════

  toggleDueDatePicker(): void {
    this.showDueDatePicker = !this.showDueDatePicker;
    this.showCompetenceDatePicker = false;
    if (this.showDueDatePicker) {
      if (this.form.dueDate) {
        const parts = this.dueDateDisplay.split('/');
        if (parts.length === 3) {
          this.dueDatePickerCurrentDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        }
      } else {
        this.dueDatePickerCurrentDate = new Date();
      }
      this.generateDueDateCalendar();
      setTimeout(() => {
        if (this.dueDateInput) {
          const rect = this.dueDateInput.nativeElement.getBoundingClientRect();
          this.dueDatePickerTop = `${rect.bottom + 8}px`;
          this.dueDatePickerLeft = `${rect.left}px`;
        }
      }, 0);
    }
  }

  generateDueDateCalendar(): void {
    const year = this.dueDatePickerCurrentDate.getFullYear();
    const month = this.dueDatePickerCurrentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();
    const days: Date[] = [];
    for (let i = startDay - 1; i >= 0; i--) days.push(new Date(year, month, -i));
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) days.push(new Date(year, month + 1, d));
    this.dueDatePickerDays = days;
  }

  dueDatePickerPreviousMonth(): void {
    this.dueDatePickerCurrentDate = new Date(
      this.dueDatePickerCurrentDate.getFullYear(),
      this.dueDatePickerCurrentDate.getMonth() - 1, 1);
    this.generateDueDateCalendar();
  }

  dueDatePickerNextMonth(): void {
    this.dueDatePickerCurrentDate = new Date(
      this.dueDatePickerCurrentDate.getFullYear(),
      this.dueDatePickerCurrentDate.getMonth() + 1, 1);
    this.generateDueDateCalendar();
  }

  getDueDatePickerMonthYear(): string {
    return this.dueDatePickerCurrentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }

  dueDatePickerIsCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.dueDatePickerCurrentDate.getMonth();
  }

  dueDatePickerIsToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  dueDatePickerIsSelected(date: Date): boolean {
    if (!this.form.dueDate) return false;
    const parts = this.dueDateDisplay.split('/');
    if (parts.length !== 3) return false;
    return date.getDate() === parseInt(parts[0]) &&
           date.getMonth() === parseInt(parts[1]) - 1 &&
           date.getFullYear() === parseInt(parts[2]);
  }

  dueDatePickerSelectDate(date: Date): void {
    const pad = (n: number) => String(n).padStart(2, '0');
    this.form.dueDate = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    this.dueDateDisplay = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
    this.showDueDatePicker = false;
  }

  dueDatePickerSelectToday(): void {
    this.dueDatePickerSelectDate(new Date());
  }

  onDueDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    if (value.length >= 2) value = value.substring(0, 2) + '/' + value.substring(2);
    if (value.length >= 5) value = value.substring(0, 5) + '/' + value.substring(5, 9);
    this.dueDateDisplay = value;
    if (value.length === 10) {
      const parts = value.split('/');
      const day = parseInt(parts[0]), month = parseInt(parts[1]), year = parseInt(parts[2]);
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const pad = (n: number) => String(n).padStart(2, '0');
        this.form.dueDate = `${year}-${pad(month)}-${pad(day)}`;
      }
    }
  }
}
